import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FileQuestion,
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  RefreshCw,
  Loader2,
  AlertTriangle,
  ArrowRight,
  UserCheck,
  UserX,
  Search as SearchIcon,
  Filter,
  History,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectClaimRequests = () => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, under_review: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  
  // Action states
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionType, setActionType] = useState(""); // approve, reject, review
  const [adminNote, setAdminNote] = useState("");
  const [processing, setProcessing] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = (statusFilter && statusFilter !== "all") ? `?status=${statusFilter}` : "";
      const res = await axios.get(`${API}/claims/${params}`);
      setRequests(res.data.requests || []);
      setStats(res.data.stats || { pending: 0, approved: 0, rejected: 0, under_review: 0 });
    } catch (error) {
      toast.error("Failed to load claim requests");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const openDetailDialog = async (claim) => {
    try {
      const res = await axios.get(`${API}/claims/${claim.id}`);
      setSelectedClaim(res.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error("Failed to load claim details");
    }
  };

  const openActionDialog = (claim, action) => {
    setSelectedClaim({ claim });
    setActionType(action);
    setAdminNote("");
    setShowActionDialog(true);
  };

  const handleAction = async () => {
    if (!selectedClaim?.claim?.id) return;

    setProcessing(true);
    try {
      const endpoint = `${API}/claims/${selectedClaim.claim.id}/${actionType}`;
      await axios.put(endpoint, { admin_note: adminNote || null });

      const messages = {
        approve: "Claim approved and project reassigned",
        reject: "Claim rejected",
        review: "Claim marked for review",
      };
      toast.success(messages[actionType]);
      setShowActionDialog(false);
      setShowDetailDialog(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: "bg-amber-100 text-amber-700", icon: Clock, label: "Pending" },
      approved: { color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2, label: "Approved" },
      rejected: { color: "bg-red-100 text-red-700", icon: XCircle, label: "Rejected" },
      under_review: { color: "bg-blue-100 text-blue-700", icon: Eye, label: "Under Review" },
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} border-0 text-[10px] font-medium`}>
        <Icon className="w-3 h-3 mr-1" />
        {c.label}
      </Badge>
    );
  };

  const StatCard = ({ label, value, color, icon: Icon }) => (
    <div className={`p-4 rounded-xl border ${color}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-xs font-medium opacity-80">{label}</p>
        </div>
        <Icon className="w-8 h-8 opacity-30" />
      </div>
    </div>
  );

  return (
    <div className="page-container" data-testid="project-claim-requests-page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <FileQuestion className="w-6 h-6 text-amber-500" />
            Project Claim Requests
          </h1>
          <p className="page-subtitle">Review and manage project reassignment requests from translators</p>
        </div>
        <Button onClick={fetchRequests} variant="outline" className="btn-secondary" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Pending" value={stats.pending} color="bg-amber-50 border-amber-200 text-amber-700" icon={Clock} />
        <StatCard label="Under Review" value={stats.under_review} color="bg-blue-50 border-blue-200 text-blue-700" icon={Eye} />
        <StatCard label="Approved" value={stats.approved} color="bg-emerald-50 border-emerald-200 text-emerald-700" icon={CheckCircle2} />
        <StatCard label="Rejected" value={stats.rejected} color="bg-red-50 border-red-200 text-red-700" icon={XCircle} />
      </div>

      {/* Filters */}
      <Card className="card mb-4">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-xs font-medium text-slate-600">Filter by Status:</span>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            {statusFilter && statusFilter !== "all" && (
              <Button variant="ghost" size="sm" onClick={() => setStatusFilter("")} className="h-8 text-xs">
                Clear Filter
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Requests Table */}
      <Card className="card">
        <CardHeader className="card-header">
          <CardTitle className="card-title">
            All Requests ({requests.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
            </div>
          ) : requests.length > 0 ? (
            <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 420px)" }}>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-[10px] font-semibold uppercase w-24">Request ID</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase">Requested By</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase">Project</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase w-20">PO</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase w-24">Job ID</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase">Current Assignee</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase text-center w-16">Dur.</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase text-center w-24">Status</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase w-24">Requested</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase text-center w-28">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((req) => (
                    <TableRow key={req.id} className={`hover:bg-slate-50/50 ${req.status === "pending" ? "bg-amber-50/30" : ""}`}>
                      <TableCell className="font-mono text-[11px] text-slate-600">{req.id}</TableCell>
                      <TableCell>
                        <p className="text-xs font-medium text-slate-700">{req.requester_name}</p>
                        <p className="text-[10px] text-slate-400 truncate max-w-[120px]">{req.requester_work_email}</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-xs font-medium text-slate-700 line-clamp-1">{req.main_title}</p>
                        <p className="text-[10px] text-slate-400 line-clamp-1">{req.episode_title}</p>
                      </TableCell>
                      <TableCell className="font-mono text-[11px] text-slate-600">{req.po_number || "-"}</TableCell>
                      <TableCell className="font-mono text-[11px] text-slate-600">{req.atlas_job_id || "-"}</TableCell>
                      <TableCell className="text-[11px] text-slate-600 truncate max-w-[120px]">
                        {req.current_assigned_email || "-"}
                      </TableCell>
                      <TableCell className="text-center font-mono text-[11px] text-slate-600">
                        {req.duration_words?.toFixed(2) || "-"}
                      </TableCell>
                      <TableCell className="text-center">{getStatusBadge(req.status)}</TableCell>
                      <TableCell className="text-[11px] text-slate-500">
                        {req.created_at ? new Date(req.created_at).toLocaleDateString() : "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDetailDialog(req)}
                            className="h-7 w-7 p-0"
                            title="View Details"
                            data-testid={`view-${req.id}`}
                          >
                            <Eye className="w-3.5 h-3.5 text-slate-500" />
                          </Button>
                          {(req.status === "pending" || req.status === "under_review") && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openActionDialog(req, "approve")}
                                className="h-7 w-7 p-0"
                                title="Approve"
                                data-testid={`approve-${req.id}`}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openActionDialog(req, "reject")}
                                className="h-7 w-7 p-0"
                                title="Reject"
                                data-testid={`reject-${req.id}`}
                              >
                                <XCircle className="w-3.5 h-3.5 text-red-500" />
                              </Button>
                              {req.status === "pending" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openActionDialog(req, "review")}
                                  className="h-7 w-7 p-0"
                                  title="Mark for Review"
                                  data-testid={`review-${req.id}`}
                                >
                                  <SearchIcon className="w-3.5 h-3.5 text-blue-500" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-16 text-center">
              <FileQuestion className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-500">No claim requests found</p>
              <p className="text-xs text-slate-400 mt-1">
                {statusFilter ? "Try clearing the filter" : "Translators haven't submitted any requests yet"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <FileQuestion className="w-4 h-4 text-amber-500" />
              Claim Request Details
            </DialogTitle>
          </DialogHeader>

          {selectedClaim?.claim && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs text-slate-500">{selectedClaim.claim.id}</span>
                {getStatusBadge(selectedClaim.claim.status)}
              </div>

              {/* Requester Info */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-[10px] font-semibold text-blue-600 uppercase mb-1">Requester</p>
                <p className="text-sm font-medium text-blue-900">{selectedClaim.claim.requester_name}</p>
                <p className="text-xs text-blue-700">{selectedClaim.claim.requester_work_email}</p>
              </div>

              {/* Project Info */}
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Project</p>
                <p className="text-sm font-semibold text-slate-800">{selectedClaim.claim.main_title}</p>
                <p className="text-xs text-slate-500">{selectedClaim.claim.episode_title}</p>
                <div className="grid grid-cols-3 gap-2 mt-2 text-[10px]">
                  <div>
                    <span className="text-slate-400">Type:</span>
                    <span className="ml-1 font-medium text-slate-600">{selectedClaim.claim.task_type}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">PO:</span>
                    <span className="ml-1 font-mono text-slate-600">{selectedClaim.claim.po_number || "-"}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Duration:</span>
                    <span className="ml-1 font-mono text-slate-600">{selectedClaim.claim.duration_words?.toFixed(2) || "-"}</span>
                  </div>
                </div>
              </div>

              {/* Assignment Change */}
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-[10px] font-semibold text-amber-600 uppercase mb-2">Requested Assignment Change</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 p-2 bg-white rounded border text-center">
                    <p className="text-[10px] text-slate-400">Current</p>
                    <p className="text-xs font-medium text-slate-700 truncate">{selectedClaim.claim.current_assigned_email || "Unknown"}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  <div className="flex-1 p-2 bg-white rounded border text-center">
                    <p className="text-[10px] text-slate-400">Requested</p>
                    <p className="text-xs font-medium text-emerald-700 truncate">{selectedClaim.claim.requester_work_email}</p>
                  </div>
                </div>
              </div>

              {/* Warnings */}
              {(selectedClaim.claim.is_invoiced || selectedClaim.claim.is_locked) && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-red-700">
                    {selectedClaim.claim.is_invoiced && <p>⚠️ This project has been invoiced</p>}
                    {selectedClaim.claim.is_locked && <p>⚠️ This project is locked in a batch</p>}
                  </div>
                </div>
              )}

              {/* Reason & Note */}
              <div className="space-y-2">
                <div>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase">Reason for Request</p>
                  <p className="text-xs text-slate-700 mt-0.5 p-2 bg-slate-50 rounded">{selectedClaim.claim.reason}</p>
                </div>
                {selectedClaim.claim.note && (
                  <div>
                    <p className="text-[10px] font-semibold text-slate-500 uppercase">Additional Note</p>
                    <p className="text-xs text-slate-700 mt-0.5 p-2 bg-slate-50 rounded">{selectedClaim.claim.note}</p>
                  </div>
                )}
              </div>

              {/* Admin Note if exists */}
              {selectedClaim.claim.admin_note && (
                <div className={`p-3 rounded-lg ${
                  selectedClaim.claim.status === "approved" 
                    ? "bg-emerald-50 border border-emerald-200" 
                    : selectedClaim.claim.status === "rejected"
                    ? "bg-red-50 border border-red-200"
                    : "bg-blue-50 border border-blue-200"
                }`}>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase">Admin Note</p>
                  <p className="text-xs mt-0.5">{selectedClaim.claim.admin_note}</p>
                  {selectedClaim.claim.decided_by && (
                    <p className="text-[10px] text-slate-400 mt-1">By: {selectedClaim.claim.decided_by}</p>
                  )}
                </div>
              )}

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-3 text-[10px] text-slate-500 pt-2 border-t">
                <div>
                  <p className="font-medium">Requested On</p>
                  <p>{selectedClaim.claim.created_at ? new Date(selectedClaim.claim.created_at).toLocaleString() : "-"}</p>
                </div>
                {selectedClaim.claim.decision_date && (
                  <div>
                    <p className="font-medium">Decision Date</p>
                    <p>{new Date(selectedClaim.claim.decision_date).toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            {selectedClaim?.claim && (selectedClaim.claim.status === "pending" || selectedClaim.claim.status === "under_review") && (
              <div className="flex gap-2 mr-auto">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowDetailDialog(false);
                    openActionDialog(selectedClaim.claim, "approve");
                  }}
                  className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowDetailDialog(false);
                    openActionDialog(selectedClaim.claim, "reject");
                  }}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <XCircle className="w-3.5 h-3.5 mr-1.5" />
                  Reject
                </Button>
              </div>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowDetailDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              {actionType === "approve" && <UserCheck className="w-4 h-4 text-emerald-500" />}
              {actionType === "reject" && <UserX className="w-4 h-4 text-red-500" />}
              {actionType === "review" && <Eye className="w-4 h-4 text-blue-500" />}
              {actionType === "approve" && "Approve Claim Request"}
              {actionType === "reject" && "Reject Claim Request"}
              {actionType === "review" && "Mark for Review"}
            </DialogTitle>
            <DialogDescription>
              {actionType === "approve" && "This will reassign the project to the requester."}
              {actionType === "reject" && "The requester will be notified of the rejection."}
              {actionType === "review" && "Mark this request for further investigation."}
            </DialogDescription>
          </DialogHeader>

          {selectedClaim?.claim && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm font-semibold text-slate-800">{selectedClaim.claim.main_title}</p>
                <p className="text-xs text-slate-500">{selectedClaim.claim.episode_title}</p>
                <div className="flex items-center gap-2 mt-2 text-[10px]">
                  <span className="text-slate-400">Requester:</span>
                  <span className="font-medium text-slate-700">{selectedClaim.claim.requester_name}</span>
                </div>
              </div>

              {actionType === "approve" && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-700">
                    <p className="font-medium">This action will:</p>
                    <ul className="mt-1 space-y-0.5 list-disc list-inside">
                      <li>Change assignment from "{selectedClaim.claim.current_assigned_email}" to "{selectedClaim.claim.requester_work_email}"</li>
                      <li>Create an audit log entry</li>
                      <li>Mark the claim as approved</li>
                    </ul>
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">
                  Admin Note {actionType === "reject" ? "(Recommended)" : "(Optional)"}
                </Label>
                <Textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder={
                    actionType === "approve" 
                      ? "Add any notes about this approval..."
                      : actionType === "reject"
                      ? "Explain why this request was rejected..."
                      : "Add notes for the review..."
                  }
                  rows={3}
                  className="text-sm"
                  data-testid="admin-note"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowActionDialog(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleAction}
              disabled={processing}
              className={
                actionType === "approve" 
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : actionType === "reject"
                  ? "bg-red-600 hover:bg-red-700 text-white"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }
              data-testid="confirm-action-btn"
            >
              {processing ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : actionType === "approve" ? (
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
              ) : actionType === "reject" ? (
                <XCircle className="w-3.5 h-3.5 mr-1.5" />
              ) : (
                <Eye className="w-3.5 h-3.5 mr-1.5" />
              )}
              {processing ? "Processing..." : actionType === "approve" ? "Approve" : actionType === "reject" ? "Reject" : "Mark for Review"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectClaimRequests;
